import { useEffect, useCallback, useState, useRef } from "react";
import "amazon-connect-streams";
import {
  CONTACT_CONNECTING_STATUS,
  CONTACT_CLOSED_STATUS,
  CONTACT_ENDED_STATUS,
  CALL_METHOD_OUTBOUND,
  AUDIO_FILE,
} from "../../constants";
import { getCcpContact, getCcpContactStatus } from "../../services/ccp";
import { getCallEndedStatus } from "../../services/outboundCall";
import { saveContactToHistoryAction } from "../../services/current";
import { getResetTimers } from "../../services/auth";
import { useAppDispatch, useAppSelector } from "../../store/types";
import ContentTabs from "./ContentTabs";
import { logMessage } from "../../utils/amplifyLogger";
import useBrowserTabClose from "../content/hooks/useBrowserTabClose";
import { getAttributesWithMissedCallStatus } from "../ccp/utils";
import { clearCCPContact } from "../../slices/ccpSlice";
import { getConnectedInstanceSelector } from "../../selectors/userInstanceSelector";
import {
  getCcpContactStatusSelector,
  getCcpInitializedSelector,
} from "../../selectors/ccpSelector";
import { getCognitoUserIdSelector } from "../../selectors/authSelector";

type ContactWithData = connect.Contact & {
  contactData?: Record<string, unknown>;
};

const Content = () => {
  const dispatch = useAppDispatch();

  const cognitoUserId = useAppSelector(getCognitoUserIdSelector);
  const isCcpInitialized = useAppSelector(getCcpInitializedSelector);
  const ccpContactStatus = useAppSelector(getCcpContactStatusSelector);
  const connectedInstance = useAppSelector(getConnectedInstanceSelector);
  const instanceUrl = connectedInstance ? connectedInstance.instanceUrl : null;

  const [contactEndedAttributes, setContactEndedAttributes] = useState<{
    ended: boolean;
    contact: connect.Contact | null;
  }>({
    ended: false,
    contact: null,
  });

  // Prevents window.connect.contact() from being registered more than once
  // per CCP session (guards against effect re-runs from isCcpInitialized flips,
  // hot-reload remounts, etc.)
  const contactHandlerRegisteredRef = useRef(false);

  // Prevents handleContactEnded from running twice for the same contact
  // because both onEnded and onDestroy fire and both call setContactEndedAttributes
  const contactEndedProcessingRef = useRef<string | null>(null);

  useEffect(() => {
    if (contactEndedAttributes?.ended) {
      handleContactEnded(contactEndedAttributes?.contact);
    }
  }, [contactEndedAttributes]);

  //useBrowserTabClose hooks setAgentOffline when the user close  windnow/tab.
  useBrowserTabClose(instanceUrl, cognitoUserId);

  const callMethod = (contact: connect.Contact) => {
    const attributes = contact?.getAttributes();
    if (!!attributes && !!attributes.Method) {
      return attributes?.Method?.value;
    }

    return "NONE";
  };

  const contactStatus = (contact: connect.Contact) => {
    const { type } = contact.getStatus();

    return type;
  };
  useEffect(() => {
    if (!isCcpInitialized) {
      // Reset the guard when CCP de-initializes so it re-registers on next init
      contactHandlerRegisteredRef.current = false;
      return;
    }
    // Guard: only register once per CCP session
    if (contactHandlerRegisteredRef.current) return;
    contactHandlerRegisteredRef.current = true;

    window.connect.contact((contact: connect.Contact) => {
      contact.onRefresh(handleContactRefresh);
      contact.onConnecting(handleContactConnecting);
      contact.onConnected(handleContactConnected);
      contact.onIncoming(handleContactIncoming);
      contact.onMissed(handleContactMissed);
      contact.onEnded((c: connect.Contact) =>
        setContactEndedAttributes({ ended: true, contact: c }),
      );
      contact.onDestroy((c: connect.Contact) =>
        setContactEndedAttributes({ ended: true, contact: c }),
      );
      contact.onError(handleContactError);
    });
    window.connect.agent((agent: connect.Agent) =>
      agent.onStateChange(handleAgentStateChange),
    );
  }, [isCcpInitialized]);

  const handleContactRefresh = useCallback(
    async (contact: connect.Contact) => {
      const contactAttributes = contact.getAttributes() as Record<
        string,
        { value: string }
      >;
      if (Object.keys(contactAttributes).length > 0) {
        // Make sure the call isn't ended
        !ccpContactStatus &&
          Object.entries(contactAttributes).length !== 0 &&
          contact.getStatus().type === CONTACT_CONNECTING_STATUS &&
          //PR: dispatch only once per contact
          (await dispatch(
            getCcpContact({
              contactId: contact.contactId,
              contactAttributes: contactAttributes,
              contactData: null,
            }),
          ));
      }
    },
    [ccpContactStatus],
  );

  const handleContactConnecting = useCallback(
    async (contact: connect.Contact) => {
      await dispatch(
        getCcpContactStatus({
          status: contactStatus(contact),
          method: callMethod(contact),
        }),
      );
    },
    [],
  );

  const handleContactConnected = useCallback(
    async (contact: connect.Contact) => {
      await dispatch(
        getCcpContactStatus({
          status: contactStatus(contact),
          method: callMethod(contact),
        }),
      );

      if (callMethod(contact) === CALL_METHOD_OUTBOUND) {
        const audio = new Audio(AUDIO_FILE.BEEP);
        audio.load();
        audio.play();
      }
    },
    [],
  );

  const handleContactEnded = async (contact: connect.Contact | null) => {
    if (!contact) {
      return;
    }
    const extContact = contact as ContactWithData;
    let audio = null;
    if (!!contact) {
      dispatch(
        getCcpContactStatus({
          status: CONTACT_CLOSED_STATUS,
          method: undefined,
        }),
      );
    }
    if (!extContact?.contactData) {
      audio = new Audio(AUDIO_FILE.CALL_ENDED);
      audio.load();
      audio.play();
      dispatch(
        getCcpContactStatus({
          status: CONTACT_ENDED_STATUS,
          method: callMethod(contact),
        }),
      );
    }
    if (contact && extContact.contactData) {
      // Guard: onEnded and onDestroy both fire — only clear once per contact.
      // Must be inside this block so the second event can still clear if the
      // first run had no contactData (e.g. onDestroy fired before onEnded).
      if (contactEndedProcessingRef.current === contact.contactId) {
        await dispatch(getCallEndedStatus());
        setContactEndedAttributes({ ended: false, contact: null });
        return;
      }
      contactEndedProcessingRef.current = contact.contactId;

      await dispatch(
        getCcpContact({
          contactId: contact.contactId,
          contactData: extContact.contactData,
        }),
      );
      await dispatch(
        saveContactToHistoryAction({
          history: true,
          historyData: extContact.contactData,
        }),
      );
      await dispatch(clearCCPContact());
    }
    await dispatch(getCallEndedStatus());
    // Do NOT reset contactEndedProcessingRef here — if onDestroy fires after
    // this async function completes, the ref still holds the contactId and
    // prevents a second execution. The next call will have a different
    // contactId so the guard will naturally allow it through.
    setContactEndedAttributes({ ended: false, contact: null });
  };

  const handleContactIncoming = useCallback(
    async (contact: connect.Contact) => {
      const extContact = contact as ContactWithData;
      await Promise.all([
        dispatch(
          getCcpContactStatus({
            status: contactStatus(contact),
            method: callMethod(contact),
          }),
        ),
        dispatch(
          getCcpContact({
            contactId: contact.contactId,
            contactAttributes: contact.getAttributes() as Record<
              string,
              { value: string }
            >,
            contactData: extContact.contactData ?? null,
          }),
        ),
      ]);
    },
    [],
  );

  const handleContactMissed = useCallback(async (contact: connect.Contact) => {
    const extContact = contact as ContactWithData;
    const contactAttributes = getAttributesWithMissedCallStatus(contact);
    dispatch(
      getCcpContact({
        contactId: contact.contactId,
        contactAttributes: contactAttributes as Record<
          string,
          { value: string }
        >,
        contactData: extContact.contactData ?? null,
      }),
    );
  }, []);

  const handleContactError = useCallback(async (contact: connect.Contact) => {
    logMessage(`Content Page`, `Contact Error. ${contact}`, `error`);
  }, []);

  const handleAgentStateChange = useCallback(
    async (agent: connect.AgentStateChange) => {
      if (agent.oldState !== agent.newState) {
        await dispatch(getResetTimers(true));
      }
    },
    [],
  );

  return <ContentTabs />;
};

export default Content;
